import { UserCheck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLeadAssignmentPolicy } from "@/hooks/useLeadAssignment";
import { cn } from "@/lib/utils";

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

/**
 * Who is responsible for a lead, wherever a lead is listed. Renders nothing
 * while the client's assignment policy is off — every assignee is null then,
 * and a column of «Sin asignar» would only be noise.
 *
 * - `text`: «Ana López» / «Sin asignar» (table cell, inbox row, mobile card).
 * - `initials`: a small round chip, «AL», with the full name in a tooltip
 *   (board card, where width is scarce). Unassigned renders nothing there. A
 *   Radix tooltip rather than `title`: the native one takes a second to show and
 *   is easy to miss on a card that grabs the pointer for dragging.
 */
export function AssigneeBadge({
  assignedProfileId,
  variant = "text",
  className,
}: {
  assignedProfileId: string | null;
  variant?: "text" | "initials";
  className?: string;
}): React.JSX.Element | null {
  const { data: policy } = useLeadAssignmentPolicy();
  if (!policy?.enabled) return null;
  const assignee = assignedProfileId
    ? (policy.members.find((m) => m.id === assignedProfileId) ?? null)
    : null;

  if (variant === "initials") {
    if (!assignee) return null;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label={`Asignado a ${assignee.name}`}
              className={cn(
                "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-ink-100 px-1 text-[9px] font-bold text-ink-600",
                className,
              )}
            >
              {initialsOf(assignee.name)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">Asignado a {assignee.name}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1 text-xs", className)}>
      <UserCheck size={12} className="shrink-0 text-ink-300" aria-hidden="true" />
      {assignee ? (
        <span className="truncate text-ink-700">{assignee.name}</span>
      ) : (
        <span className="italic text-ink-400">Sin asignar</span>
      )}
    </span>
  );
}
